import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Spin } from 'pink-lava-ui';
import { Image, Progress, Upload, message, type UploadFile } from 'antd';
import { PlusCircleIcon, XIcon } from 'lucide-react';
import { uploadFiles, type UploadError } from '@dc-trans/data-layer';
import './styles.css';
import { useEffect, useRef, useState } from 'react';
import type { RcFile } from 'antd/es/upload';

interface AttachmentInputProps {
  name: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  showErrorMessage?: boolean;
  variant?: 'filled' | 'borderless' | 'underlined' | 'outlined';
  accept?: string;
  onChange?: (_value: UploadFile[], _fieldOnChange: (_value: UploadFile[]) => void) => void;
  onChangeCallback?: (_value: UploadFile[], _setValue: (_name: string, _value: any) => void) => void;
  maxConcurrentUploads?: number;
  maxFileSizeMB?: number;
  onUploadComplete?: (_fileKeys: string[]) => void;
  onUploadError?: (_error: string) => void;
  info?: string;
}

export const AttachmentInput = ({
  name,
  label,
  disabled = false,
  accept = 'image/jpeg, image/png',
  required = false,
  showErrorMessage = true,
  maxConcurrentUploads = 3,
  maxFileSizeMB = 10,
  info,
}: AttachmentInputProps) => {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();
  // Use useFieldArray to manage the attachments array
  const { append, remove } = useFieldArray({
    control,
    name,
  });

  const fileIdToUploadIdMap = useRef<{ [id: string]: string }>({});
  const pendingUploadsRef = useRef<Set<string>>(new Set());
  const [uiFileList, setUiFileList] = useState<UploadFile[]>([]);

  const error = errors[name];

  // Clear UI file list when form value resets/clears
  const watchedAttachments = watch(name);
  useEffect(() => {
    if (!watchedAttachments || watchedAttachments.length === 0) {
      setUiFileList([]);
      fileIdToUploadIdMap.current = {};
      pendingUploadsRef.current.clear();
    }
  }, [watchedAttachments]);

  // Convert MB to bytes for validation
  const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1 font-nunito">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          return (
            <div>
              <Upload
                listType="picture-card"
                disabled={disabled}
                maxCount={maxConcurrentUploads}
                accept={accept}
                fileList={uiFileList}
                beforeUpload={(file, fileList) => {
                  // File extension validation based on accept prop
                  if (accept) {
                    const acceptedTypes = accept.split(',').map(type => type.trim().toLowerCase());
                    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
                    const fileMimeType = file.type.toLowerCase();
                    // Check if file matches accepted MIME types or extensions
                    const isValidType = acceptedTypes.some(acceptedType => {
                      if (acceptedType.startsWith('.')) {
                        // Extension-based check (e.g., .jpg, .png)
                        return fileExtension === acceptedType;
                      } else if (acceptedType.includes('/')) {
                        // MIME type check (e.g., image/jpeg, image/png)
                        return fileMimeType === acceptedType || fileMimeType.startsWith(acceptedType.replace('*', ''));
                      } else if (acceptedType === 'image') {
                        // Generic image check
                        return fileMimeType.startsWith('image/');
                      } else if (acceptedType === 'video') {
                        // Generic video check
                        return fileMimeType.startsWith('video/');
                      } else if (acceptedType === 'audio') {
                        // Generic audio check
                        return fileMimeType.startsWith('audio/');
                      }
                      return false;
                    });

                    if (!isValidType) {
                      message.error(`Tipe file tidak didukung. Hanya file dengan format ${accept} yang diizinkan.`);
                      return Upload.LIST_IGNORE;
                    }
                  }

                  // File size validation
                  if (file.size > maxFileSizeBytes) {
                    message.error(`Ukuran file harus kurang dari ${maxFileSizeMB}MB`);
                    return Upload.LIST_IGNORE;
                  }

                  // Limit the number of files that can be uploaded
                  const currentFileCount = (field.value || []).length;
                  const totalFilesAfterUpload = currentFileCount + fileList.length;

                  if (totalFilesAfterUpload > maxConcurrentUploads) {
                    const allowedFiles = maxConcurrentUploads - currentFileCount;
                    if (allowedFiles <= 0) {
                      message.error(`Maximum ${maxConcurrentUploads} files allowed`);
                      return Upload.LIST_IGNORE;
                    }
                    // Only allow the first few files that fit within the limit
                    const fileIndex = fileList.indexOf(file);
                    if (fileIndex >= allowedFiles) {
                      return Upload.LIST_IGNORE;
                    }
                  }
                  return true;
                }}
                customRequest={options => {
                  const { file, onProgress, onSuccess, onError } = options;
                  const fileUid = (file as RcFile).uid;

                  // Track this upload as pending
                  pendingUploadsRef.current.add(fileUid);

                  // Add file to UI list and mark as uploading
                  setUiFileList(prev => [
                    ...prev,
                    {
                      uid: fileUid,
                      name: (file as RcFile).name,
                      status: 'uploading',
                      percent: 0,
                      originFileObj: file as RcFile,
                    },
                  ]);

                  // Call the upload function directly
                  uploadFiles([file as File], {
                    maxConcurrentUploads,
                    onProgress: (_fileId, progress) => {
                      // Use the FileUploader's onProgress callback
                      onProgress?.({ percent: progress.percentage });
                      setUiFileList(prev =>
                        prev.map(f => (f.uid === fileUid ? { ...f, percent: progress.percentage } : f))
                      );
                    },
                    onError: (error: UploadError) => {
                      pendingUploadsRef.current.delete(fileUid);
                      setUiFileList(prev => prev.map(f => (f.uid === fileUid ? { ...f, status: 'error' } : f)));
                      onError?.(error);
                    },
                  })
                    .then(fileKeys => {
                      const uploadId = fileKeys[0];
                      fileIdToUploadIdMap.current[fileUid] = uploadId;

                      append(uploadId);
                      setUiFileList(prev =>
                        prev.map(f =>
                          f.uid === fileUid
                            ? {
                                ...f,
                                status: 'done',
                                percent: 100,
                                thumbUrl: URL.createObjectURL(file as Blob),
                              }
                            : f
                        )
                      );
                      onSuccess?.(file);
                    })
                    .catch(error => {
                      pendingUploadsRef.current.delete(fileUid);
                      setUiFileList(prev => prev.map(f => (f.uid === fileUid ? { ...f, status: 'error' } : f)));
                      onError?.(error);
                    });

                  // Return an abort function
                  return {
                    abort: () => {
                      pendingUploadsRef.current.delete(fileUid);
                      console.log('Upload aborted for:', (file as File).name);
                    },
                  };
                }}
                onRemove={file => {
                  // Find the index of the file to remove
                  const fileId = fileIdToUploadIdMap.current[file.uid];
                  const indexToRemove = field.value.findIndex((item: string) => item === fileId);

                  if (indexToRemove !== -1) {
                    // Remove the file using useFieldArray's remove method
                    remove(indexToRemove);
                  }
                  // Update UI file list
                  setUiFileList(prev => prev.filter(f => f.uid !== file.uid));
                }}
                multiple
                itemRender={(_originNode, file, _fileList, actions) => {
                  return (
                    <div className="relative">
                      <div
                        key={file.uid}
                        className={`w-14 h-14 rounded-xl overflow-hidden ${file.status === 'error' ? 'border-red-500' : 'border-gray-300'} border-1 flex flex-col items-center justify-center`}
                      >
                        {file.status === 'uploading' ? (
                          <Spin size={'large'} />
                        ) : (
                          file.status === 'done' &&
                          file.thumbUrl && (
                            <Image alt={file.name} src={URL.createObjectURL(file.originFileObj as Blob)} />
                          )
                        )}
                        <button
                          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center border-2 border-white cursor-pointer"
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            actions.remove();
                          }}
                        >
                          <XIcon className="text-white" size={16} />
                        </button>
                      </div>
                      {file.status === 'uploading' && <Progress percent={file.percent} showInfo={false} />}
                    </div>
                  );
                }}
              >
                {(field.value || []).length < maxConcurrentUploads && (
                  <div
                    className={`w-14 h-14 rounded-xl ${error ? 'border-red-500' : 'border-gray-300'} border-[1px] flex items-center justify-center cursor-pointer`}
                  >
                    <PlusCircleIcon color={'#EB008B'} />
                  </div>
                )}
              </Upload>
            </div>
          );
        }}
      />

      {info && <div className="text-gray-500 text-xs mt-1">{info}</div>}
      {showErrorMessage && error?.message && (
        <div className="text-red-500 text-xs mt-1">{error.message.toString()}</div>
      )}
    </div>
  );
};
